// src/components/MapView.jsx
// Google Maps version — AdvancedMarkerElement with HTML pill/dot markers.
import { useEffect, useRef, useState } from "react";

import { getFollowingColor, SHARED_CAT_COLOR } from "../constants";

const MY_PRIMARY = "#655d54";

const STATUS_DOT = {
  want_to_go:      "#BA7517",
  visited:         "#1D9E75",
  want_revisit:    "#D4537E",
};

const isSameLocation = (a, b, threshold = 0.0001) =>
  Math.abs(a.lat - b.lat) < threshold && Math.abs(a.lng - b.lng) < threshold;

/* ── HTML → DOM helper ───────────────────────────────────────── */

const htmlToEl = (html) => {
  const d = document.createElement("div");
  d.innerHTML = html.trim();
  return d.firstChild;
};

/* ── Marker HTML ─────────────────────────────────────────────── */

const myMarker = ({ name, status, shared = false, folderColor }) => {
  const bg = folderColor || STATUS_DOT[status] || MY_PRIMARY;
  return `
    <div class="map-pill" style="
      display:inline-flex;align-items:center;
      background:${bg};
      color:#fff6ef;
      padding:4px 9px;
      border-radius:6px;
      font-family:'Manrope',-apple-system,sans-serif;
      font-size:11px;font-weight:600;
      white-space:nowrap;
      box-shadow:0 2px 10px rgba(0,0,0,0.18);
      cursor:pointer;
      letter-spacing:0.01em;
      ${shared ? "outline:1.5px solid rgba(255,246,239,0.5);outline-offset:1px;" : ""}
    ">
      <span>${name}</span>
    </div>
  `;
};

const followingMarker = ({ name, color, nickname }) => `
  <div class="map-pill" style="
    display:inline-flex;align-items:center;gap:5px;
    background:rgba(255,255,255,0.92);
    color:${color};
    padding:4px 9px 4px 4px;
    border-radius:6px;
    border:1.5px solid ${color};
    font-family:'Manrope',-apple-system,sans-serif;
    font-size:11px;font-weight:600;
    white-space:nowrap;
    box-shadow:0 2px 8px rgba(0,0,0,0.08);
    cursor:pointer;
    letter-spacing:0.01em;
  ">
    <span style="
      width:18px;height:18px;border-radius:50%;
      background:${color};
      display:inline-flex;align-items:center;justify-content:center;
      font-family:'Noto Serif',Georgia,serif;font-style:italic;
      font-size:9px;color:white;font-weight:700;flex-shrink:0;
    ">${nickname?.[0]?.toUpperCase() || '?'}</span>
    <span>${name}</span>
  </div>
`;

const blogMarker = ({ name, color }) => `
  <div class="map-pill" style="
    display:inline-flex;align-items:center;
    background:${color};
    color:white;
    padding:4px 9px;
    border-radius:6px;
    font-family:'Manrope',-apple-system,sans-serif;
    font-size:11px;font-weight:600;
    white-space:nowrap;
    box-shadow:0 2px 10px rgba(0,0,0,0.15);
    cursor:pointer;
    letter-spacing:0.01em;
  ">${name}</div>
`;

const sharedMarker = ({ name, count, category }) => {
  const color = SHARED_CAT_COLOR[category] || "#655d54";
  return `
  <div class="map-pill" style="
    display:inline-flex;align-items:center;gap:4px;
    background:${color};
    color:#fff6ef;
    padding:4px 9px;
    border-radius:6px;
    font-family:'Manrope',-apple-system,sans-serif;
    font-size:11px;
    white-space:nowrap;
    box-shadow:0 2px 10px rgba(0,0,0,0.18);
    cursor:pointer;
    letter-spacing:0.01em;
  ">
    <span style="font-weight:800">(${count})</span>
    <span style="font-weight:600">${name}</span>
  </div>
`;
};

const ZOOM_THRESHOLD = 12;

const dotMarker = (color) => `
  <div style="
    width:12px;height:12px;border-radius:50%;
    background:${color};
    border:2px solid rgba(255,255,255,0.8);
    box-shadow:0 1px 4px rgba(0,0,0,0.25);
  "></div>
`;

export default function MapView({
  restaurants, personalPlaces = [], accounts, onMarkerClick, onMapReady,
  followingPlaces = [], onFollowingMarkerClick, folders = [],
  focusPlace = null,
  mapMode = "personal", sharedPlaces = [], onSharedMarkerClick,
  activeTrip = null, // { name, stops: [{ lat, lng, name, time, thumbnailUrl }] }
}) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const personalMarkersRef = useRef([]);
  const followingMarkersRef = useRef([]);
  const sharedMarkersRef = useRef([]);
  const [mapReady, setMapReady] = useState(false);
  const hasFitBounds = useRef(false);
  const activeMarkerRef = useRef(null);
  const zoomRef = useRef(13);
  const zCounterRef = useRef(100);

  /* ── z-index helpers ───────────────────────────────────────── */

  const setMarkerZ = (marker, z) => {
    marker.zIndex = z;
  };

  const attachHoverZ = (marker) => {
    const el = marker.element;
    if (!el || el._hoverZAttached) return;
    el.addEventListener("mouseenter", () => {
      zCounterRef.current += 1;
      setMarkerZ(marker, zCounterRef.current);
    });
    el.addEventListener("touchstart", () => {
      zCounterRef.current += 1;
      setMarkerZ(marker, zCounterRef.current);
    }, { passive: true });
    el._hoverZAttached = true;
  };

  const bringToFront = (marker) => {
    if (activeMarkerRef.current && activeMarkerRef.current !== marker) {
      if (zoomRef.current < ZOOM_THRESHOLD && activeMarkerRef.current._dotHtml) {
        activeMarkerRef.current.content = htmlToEl(activeMarkerRef.current._dotHtml);
      }
    }
    if (marker._pillHtml) marker.content = htmlToEl(marker._pillHtml);
    zCounterRef.current += 1;
    marker.zIndex = zCounterRef.current;
    requestAnimationFrame(() => attachHoverZ(marker));
    activeMarkerRef.current = marker;
  };

  const updateMarkerIcons = (showPill) => {
    const allMarkers = [
      ...markersRef.current,
      ...personalMarkersRef.current,
      ...followingMarkersRef.current,
      ...sharedMarkersRef.current,
    ];
    allMarkers.forEach((m) => {
      if (m === activeMarkerRef.current) return;
      m.content = htmlToEl(showPill ? m._pillHtml : m._dotHtml);
      requestAnimationFrame(() => attachHoverZ(m));
    });
  };

  /* ── Helpers ────────────────────────────────────────────────── */

  const getFolderColor = (folderId) => {
    if (!folderId) return null;
    const f = folders.find((x) => x.id === folderId);
    return f?.color || null;
  };

  const getAccountColor = (accountId) => {
    const COLORS = ["#E8593C","#3B8BD4","#1D9E75","#BA7517","#7F77DD","#D4537E","#0F6E56","#993C1D"];
    const idx = accounts.findIndex((a) => a.id === accountId);
    return COLORS[idx % COLORS.length] || "#777c77";
  };

  const createMarker = ({ lat, lng, pillHtml, dotHtml, zIndex = 1, onClick }) => {
    const isPill = zoomRef.current >= ZOOM_THRESHOLD;
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position: { lat, lng },
      map: mapInstance.current,
      content: htmlToEl(isPill ? pillHtml : dotHtml),
      zIndex,
      gmpClickable: true,
    });
    marker._pillHtml = pillHtml;
    marker._dotHtml = dotHtml;
    marker.addListener("click", () => {
      bringToFront(marker);
      panToPlace(lat, lng);
      onClick();
    });
    requestAnimationFrame(() => attachHoverZ(marker));
    return marker;
  };

  const removeMarkers = (arr) => {
    arr.forEach((m) => { m.map = null; });
    arr.length = 0;
  };

  /* ── Load Google Maps SDK dynamically ────────────────────── */

  useEffect(() => {
    // Use first personal place as fallback center, otherwise New York
    const firstPlace = personalPlaces.length > 0 ? personalPlaces[0] : null;
    const FALLBACK = firstPlace && firstPlace.lat && firstPlace.lng
      ? { lat: firstPlace.lat, lng: firstPlace.lng }
      : { lat: 40.7128, lng: -74.006 };

    const initMap = (lat, lng) => {
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: { lat, lng },
        zoom: 13,
        minZoom: 2,
        restriction: { latLngBounds: { north: 85, south: -85, west: -180, east: 180 }, strictBounds: false },
        mapId: process.env.REACT_APP_GOOGLE_MAP_ID || undefined,
        disableDefaultUI: true,
        zoomControl: false,
        gestureHandling: "greedy",
      });
      mapInstance.current.addListener("zoom_changed", () => {
        const zoom = mapInstance.current.getZoom();
        const wasPill = zoomRef.current >= ZOOM_THRESHOLD;
        const isPill = zoom >= ZOOM_THRESHOLD;
        zoomRef.current = zoom;
        if (wasPill !== isPill) updateMarkerIcons(isPill);
      });
      setMapReady(true);
      if (onMapReady) onMapReady(mapInstance.current);
    };

    const startWithLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => initMap(pos.coords.latitude, pos.coords.longitude),
          () => initMap(FALLBACK.lat, FALLBACK.lng),
          { timeout: 3000, maximumAge: 300000 }
        );
      } else {
        initMap(FALLBACK.lat, FALLBACK.lng);
      }
    };

    // If already loaded (e.g. hot reload), init immediately
    if (window.google?.maps?.Map) {
      startWithLocation();
      return;
    }

    // Prevent duplicate script injection (React StrictMode runs effects twice)
    if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) { clearInterval(check); startWithLocation(); }
      }, 100);
      return () => clearInterval(check);
    }

    const script = document.createElement("script");
    const lang = localStorage.getItem("lang") || "en";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&libraries=marker&v=weekly&loading=async&language=${lang}`;
    script.async = true;
    script.onload = () => {
      // With loading=async, google.maps.Map may not be ready on script.onload
      if (window.google?.maps?.Map) {
        startWithLocation();
      } else {
        const check = setInterval(() => {
          if (window.google?.maps?.Map) { clearInterval(check); startWithLocation(); }
        }, 100);
      }
    };
    document.head.appendChild(script);
  }, []); // eslint-disable-line

  /* ── Pan to place — offset for panel/sheet ─────────────────── */

  const panToPlace = (lat, lng) => {
    const map = mapInstance.current;
    if (!map) return;

    const isMob = window.innerWidth <= 768;
    map.panTo({ lat, lng });

    // Offset so the marker is centered in the visible area (not behind panel/sheet)
    if (isMob) {
      map.panBy(0, 137); // half of bottom sheet+tab ~274px
    } else {
      map.panBy(-180, 0); // half of left panel 360px
    }
  };

  /* ── Show/hide markers on mode switch ──────────────────────── */

  useEffect(() => {
    if (!mapReady) return;
    const isPersonal = mapMode === "personal";
    markersRef.current.forEach((m) => { m.map = isPersonal ? mapInstance.current : null; });
    personalMarkersRef.current.forEach((m) => { m.map = mapInstance.current; });
    followingMarkersRef.current.forEach((m) => { m.map = isPersonal ? mapInstance.current : null; });
    sharedMarkersRef.current.forEach((m) => { m.map = !isPersonal ? mapInstance.current : null; });
  }, [mapMode, mapReady]);

  /* ── Blogger place markers ─────────────────────────────────── */

  useEffect(() => {
    if (!mapReady) return;
    removeMarkers(markersRef.current);
    zCounterRef.current = 100;
    if (restaurants.length === 0) hasFitBounds.current = false;

    restaurants.forEach((r) => {
      const mentions = r.account_mentions || [];
      const color = mentions.length > 0 ? getAccountColor(mentions[0].account_id) : "#777c77";
      const marker = createMarker({
        lat: r.lat, lng: r.lng,
        pillHtml: blogMarker({ name: r.name, color }),
        dotHtml: dotMarker(color),
        onClick: () => onMarkerClick(r.id, false),
      });
      markersRef.current.push(marker);
    });

    if (restaurants.length > 0 && !hasFitBounds.current) {
      const bounds = new window.google.maps.LatLngBounds();
      restaurants.forEach((r) => bounds.extend({ lat: r.lat, lng: r.lng }));
      mapInstance.current.fitBounds(bounds, 60);
      hasFitBounds.current = true;
    }
  }, [restaurants, accounts, mapReady]); // eslint-disable-line

  /* ── My places markers ─────────────────────────────────────── */

  useEffect(() => {
    if (!mapReady) return;
    removeMarkers(personalMarkersRef.current);
    zCounterRef.current = 100;

    const allFollowing = followingPlaces.flatMap(({ places, nickname, colorIdx }) =>
      places.map((p) => ({ ...p, _nickname: nickname, _colorIdx: colorIdx }))
    );

    personalPlaces.forEach((r) => {
      const sharedWith = allFollowing.filter((fp) => {
        if (r.google_place_id && fp.google_place_id) return r.google_place_id === fp.google_place_id;
        return isSameLocation(r, fp);
      });

      const folderColor = getFolderColor(r.folder_id);
      const dotColor = folderColor || STATUS_DOT[r.status] || MY_PRIMARY;
      const marker = createMarker({
        lat: r.lat, lng: r.lng,
        pillHtml: myMarker({ name: r.name, status: r.status, shared: sharedWith.length > 0, folderColor }),
        dotHtml: dotMarker(dotColor),
        zIndex: sharedWith.length > 0 ? 2 : 1,
        onClick: () => onMarkerClick(`personal_${r.id}`, true),
      });
      personalMarkersRef.current.push(marker);
    });
  }, [personalPlaces, followingPlaces, folders, mapReady]); // eslint-disable-line

  /* ── Following markers ─────────────────────────────────────── */

  useEffect(() => {
    if (!mapReady) return;
    removeMarkers(followingMarkersRef.current);
    zCounterRef.current = 100;

    followingPlaces.forEach(({ userId, nickname, colorIdx, places }) => {
      const color = getFollowingColor(colorIdx);
      places.forEach((r) => {
        const isDup = personalPlaces.some((p) => {
          if (p.google_place_id && r.google_place_id) return p.google_place_id === r.google_place_id;
          return isSameLocation(p, r);
        });
        if (isDup) return;

        const marker = createMarker({
          lat: r.lat, lng: r.lng,
          pillHtml: followingMarker({ name: r.name, color, nickname }),
          dotHtml: dotMarker(color),
          onClick: () => {
            if (onFollowingMarkerClick) onFollowingMarkerClick({ ...r, ownerNickname: nickname, ownerUserId: userId });
          },
        });
        followingMarkersRef.current.push(marker);
      });
    });
  }, [followingPlaces, personalPlaces, mapReady]); // eslint-disable-line

  /* ── Our Space markers ─────────────────────────────────────── */

  useEffect(() => {
    if (!mapReady) return;
    removeMarkers(sharedMarkersRef.current);

    if (mapMode !== "shared") return;

    sharedPlaces.forEach((p) => {
      const catColor = SHARED_CAT_COLOR[p.category] || MY_PRIMARY;
      const marker = createMarker({
        lat: p.lat, lng: p.lng,
        pillHtml: sharedMarker({ name: p.name, count: p.pick_count, category: p.category }),
        dotHtml: dotMarker(catColor),
        zIndex: p.pick_count,
        onClick: () => { if (onSharedMarkerClick) onSharedMarkerClick(p); },
      });
      sharedMarkersRef.current.push(marker);
    });
  }, [sharedPlaces, mapMode, mapReady]); // eslint-disable-line

  /* ── Trip route (polyline + thumbnail markers) ─────────────── */

  const tripMarkersRef = useRef([]);
  const tripPolylineRef = useRef(null);
  const tripOverlayRef = useRef(null);

  useEffect(() => {
    // Clean up previous trip
    removeMarkers(tripMarkersRef.current);
    if (tripPolylineRef.current) { tripPolylineRef.current.setMap(null); tripPolylineRef.current = null; }
    if (tripOverlayRef.current) { tripOverlayRef.current.remove(); tripOverlayRef.current = null; }

    if (!mapReady || !activeTrip || !activeTrip.stops?.length) return;

    const stops = activeTrip.stops;
    const TRIP_COLOR = "#E8943A";

    // Draw polyline
    const path = stops.map((s) => ({ lat: s.lat, lng: s.lng }));
    tripPolylineRef.current = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: TRIP_COLOR,
      strokeOpacity: 0.8,
      strokeWeight: 3,
      map: mapInstance.current,
    });

    // Create markers for each stop
    stops.forEach((stop, i) => {
      const thumbSize = 36;
      const timeLabel = stop.time || "";
      const pillHtml = `
        <div style="
          display:flex;flex-direction:column;align-items:center;gap:2px;
          cursor:pointer;
        ">
          <div style="
            display:flex;align-items:center;gap:6px;
            background:rgba(255,255,255,0.95);
            padding:4px 10px 4px 4px;
            border-radius:8px;
            border:2px solid ${TRIP_COLOR};
            box-shadow:0 2px 10px rgba(0,0,0,0.15);
          ">
            <div style="
              width:${thumbSize}px;height:${thumbSize}px;border-radius:6px;
              background:url('${stop.thumbnailUrl || ""}') center/cover no-repeat;
              background-color:#ede0d5;
              flex-shrink:0;
              display:flex;align-items:center;justify-content:center;
              font-family:'Manrope',sans-serif;font-size:14px;font-weight:800;
              color:${TRIP_COLOR};
            ">${stop.thumbnailUrl ? "" : (i + 1)}</div>
            <div style="min-width:0;">
              <p style="margin:0;font-family:'Manrope',sans-serif;font-size:11px;font-weight:700;
                color:#2f3430;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:120px;">
                ${stop.name || `Stop ${i + 1}`}
              </p>
              ${timeLabel ? `<p style="margin:0;font-family:'Manrope',sans-serif;font-size:9px;color:#8a8e8a;">
                ${timeLabel}
              </p>` : ""}
            </div>
          </div>
        </div>
      `;
      const dotHtml = `
        <div style="
          width:20px;height:20px;border-radius:50%;
          background:${TRIP_COLOR};border:2.5px solid #fff;
          box-shadow:0 2px 6px rgba(0,0,0,0.25);
          display:flex;align-items:center;justify-content:center;
          font-family:'Manrope',sans-serif;font-size:10px;font-weight:800;color:#fff;
        ">${i + 1}</div>
      `;
      const marker = createMarker({
        lat: stop.lat, lng: stop.lng,
        pillHtml, dotHtml,
        zIndex: 50 + i,
        onClick: () => {},
      });
      tripMarkersRef.current.push(marker);
    });

    // Fit bounds to show entire trip
    const bounds = new window.google.maps.LatLngBounds();
    stops.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
    mapInstance.current.fitBounds(bounds, 60);

  }, [activeTrip, mapReady]); // eslint-disable-line

  /* ── Focus marker (from feed deep link) ────────────────────── */

  const focusMarkerRef = useRef(null);
  useEffect(() => {
    if (focusMarkerRef.current) { focusMarkerRef.current.map = null; focusMarkerRef.current = null; }
    if (!mapReady || !focusPlace || !window.google) return;

    const allMarkers = [...personalMarkersRef.current, ...followingMarkersRef.current, ...sharedMarkersRef.current];
    const exists = allMarkers.some((m) => {
      const pos = m.position;
      return Math.abs(pos.lat - focusPlace.lat) < 0.0001 && Math.abs(pos.lng - focusPlace.lng) < 0.0001;
    });
    if (exists) return;

    const color = focusPlace.owner_nickname ? "#8B7E74" : MY_PRIMARY;
    const inner = focusPlace.owner_nickname
      ? followingMarker({ name: focusPlace.name, color, nickname: focusPlace.owner_nickname })
      : myMarker({ name: focusPlace.name, status: focusPlace.status });

    focusMarkerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
      position: { lat: focusPlace.lat, lng: focusPlace.lng },
      map: mapInstance.current,
      content: htmlToEl(inner),
      zIndex: 10,
    });
  }, [focusPlace, mapReady]); // eslint-disable-line

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <style>{`
        .map-pill { transition: transform 0.1s; }
        .map-pill:hover { transform: scale(1.03); }
      `}</style>
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      {!mapReady && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "#f4f4f0",
          fontFamily: "'Manrope', -apple-system, sans-serif", fontSize: 12,
          color: "#a8a29e", letterSpacing: "0.08em",
        }}>
          Loading...
        </div>
      )}
    </div>
  );
}
